import {
  CreateBoxExceptionConditionPayload,
  PackagingRuleField,
  PackagingRuleOperator,
} from '../../models/PackagingRulesets';

export interface ConditionFormRow {
  id: string;
  joinWithPrevious: 'AND' | 'OR';
  field: PackagingRuleField;
  operator: PackagingRuleOperator;
  value: string;
  boolValue: boolean;
}

export const NUMERIC_COMPARISON_FIELDS: PackagingRuleField[] = ['total_amount', 'weight_kg'];
export const ID_NUMERIC_FIELDS: PackagingRuleField[] = ['product_id', 'pack_id'];
export const NUMERIC_FIELDS: PackagingRuleField[] = [...NUMERIC_COMPARISON_FIELDS, ...ID_NUMERIC_FIELDS];
export const COUNTRY_FIELDS: PackagingRuleField[] = ['country'];
export const BOOLEAN_FIELDS: PackagingRuleField[] = ['fragile'];
export const ID_OPERATORS: PackagingRuleOperator[] = ['=', '!='];

export const createConditionRow = (joinWithPrevious: 'AND' | 'OR' = 'AND'): ConditionFormRow => ({
  id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
  joinWithPrevious,
  field: 'total_amount',
  operator: '>',
  value: '',
  boolValue: false,
});

export const conditionToRow = (
  condition: CreateBoxExceptionConditionPayload,
  prevGroupNo: number | null,
): ConditionFormRow => {
  const groupNo = condition.group_no ?? 0;
  return {
    id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    joinWithPrevious: prevGroupNo === null ? 'AND' : groupNo !== prevGroupNo ? 'OR' : 'AND',
    field: (condition.field as PackagingRuleField) ?? 'total_amount',
    operator: condition.operator,
    value: condition.value_number !== undefined ? String(condition.value_number) : (condition.value_string ?? ''),
    boolValue: Boolean(condition.value_boolean),
  };
};

export const isNumericField = (row: ConditionFormRow) => NUMERIC_FIELDS.includes(row.field);
export const isIdField = (row: ConditionFormRow) => ID_NUMERIC_FIELDS.includes(row.field);
export const isCountryField = (row: ConditionFormRow) => COUNTRY_FIELDS.includes(row.field);
export const isBooleanField = (row: ConditionFormRow) => BOOLEAN_FIELDS.includes(row.field);

export const isConditionRowValid = (row: ConditionFormRow): boolean => {
  if (isBooleanField(row)) return true;
  return row.value.trim().length > 0;
};

export const buildConditionFromRow = (row: ConditionFormRow, groupNo: number): CreateBoxExceptionConditionPayload => {
  const condition: CreateBoxExceptionConditionPayload = {
    field: row.field,
    operator: row.operator,
    group_no: groupNo,
    value_type: isNumericField(row) ? 'number' : isBooleanField(row) ? 'boolean' : 'string',
  };
  if (isNumericField(row)) condition.value_number = Number(row.value);
  else if (isBooleanField(row)) condition.value_boolean = row.boolValue;
  else condition.value_string = row.value;
  return condition;
};

export const buildFinalConditions = (conditionRows: ConditionFormRow[]): CreateBoxExceptionConditionPayload[] => {
  let groupNo = 0;
  const result: CreateBoxExceptionConditionPayload[] = [];
  conditionRows.forEach((row, idx) => {
    if (!isConditionRowValid(row)) return;
    if (idx > 0 && row.joinWithPrevious === 'OR') groupNo += 1;
    result.push(buildConditionFromRow(row, groupNo));
  });
  return result;
};

export const getDefaultOperatorForField = (field: PackagingRuleField): PackagingRuleOperator => {
  if (NUMERIC_COMPARISON_FIELDS.includes(field)) return '>';
  if (ID_NUMERIC_FIELDS.includes(field)) return '=';
  return '=';
};
